import { MapPin } from 'lucide-react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useFormAddress } from './hooks/useFormAddress';

interface ReadOnlyInputProps {
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
}

const ReadOnlyFormField = ({
  name,
  label,
  placeholder,
  required,
}: ReadOnlyInputProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const error = errors[name];

  return (
    <div className="space-y-2">
      <Label htmlFor={name} className="text-sm">
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <Input
        id={name}
        disabled
        {...register(name, {
          required: required ? `${label} is required` : false,
        })}
        placeholder={placeholder}
        className="bg-bg"
      />
      {error && (
        <p className="text-sm font-medium text-destructive">
          {error.message?.toString()}
        </p>
      )}
    </div>
  );
};

interface AdressSectionProps {
  methods: UseFormReturn<any>;
  verifiedAddress: boolean;
}

export const AddressSection = ({
  methods,
  verifiedAddress,
}: AdressSectionProps) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  const longitude = watch('lon');
  const latitude = watch('lat');

  const { handleAddressInputFocus, handleAddressSelected } = useFormAddress();

  return (
    <div className="pt-2 border-t">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium">Location Information</h3>
        {verifiedAddress && (
          <div className="flex items-center text-sm text-green-600">
            <MapPin className="h-3 w-3 mr-1" />
            Address Verified
          </div>
        )}
      </div>

      <AddressAutocomplete
        label="Search Address"
        required
        onAddressSelected={handleAddressSelected}
        error={errors.address1?.message?.toString()}
        onFocus={handleAddressInputFocus}
      />

      {longitude && latitude && (
        <div className="bg-bg p-3 rounded-md mt-4 border space-y-3">
          <ReadOnlyFormField
            name="address1"
            label="Address Line 1"
            placeholder="Street address"
          />
          <ReadOnlyFormField
            name="address2"
            label="Address Line 2"
            placeholder="Apartment, suite, etc."
          />
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyFormField name="city" label="City" placeholder="City" />
            <ReadOnlyFormField name="state" label="State" placeholder="State" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyFormField
              name="country"
              label="Country"
              placeholder="Country"
            />
            <ReadOnlyFormField
              name="zipcode"
              label="Zipcode"
              placeholder="Zipcode"
            />
          </div>
        </div>
      )}
    </div>
  );
};
